import React from "react";
import { useWeb3 } from "../context/Web3Context";
import Register from "../components/Register";
import Welcome from "../components/Welcome";
import ClaimBanner from "../components/ClaimBanner";
import Donut from "../components/charts/Donut";
import CountUp from "../components/common/CountUp";
import CopyField from "../components/common/CopyField";
import RecentActivity from "../components/RecentActivity";
import SectionHeader from "../components/common/SectionHeader";
import Icon from "../components/common/Icon";
import { bnToNumber, fmtUsdt, shortAddress } from "../utils/format";
import { PACKAGES } from "../utils/constants";
import "./Home.css";

export default function Home({ data, refresh, goTab }) {
  const { account } = useWeb3();

  if (!data.registered) {
    return (
      <div className="stack">
        <Register usdtBal={data.usdtBal} onDone={refresh} />
        <Welcome />
      </div>
    );
  }

  const activePkgs = (data.packages || []).map(Boolean);
  // Highest activated package only.
  let highest = null;
  for (let i = PACKAGES.length - 1; i >= 0; i--) {
    if (activePkgs[i]) { highest = PACKAGES[i]; break; }
  }

  // On-chain values are NET (after the 10% deduction). Show GROSS = net / 0.9.
  const GROSS = 10 / 9;
  const grossTotalBN = data.details.totalIncome.mul(10).div(9);
  const totalIncome = bnToNumber(data.details.totalIncome) * GROSS;
  const direct = bnToNumber(data.income.directReferralIncome) * GROSS;
  const gen = bnToNumber(data.income.normalLevelIncome) * GROSS;
  const matrix = bnToNumber(data.income.matrixLevelIncome) * GROSS;
  const incomeTotal = direct + gen + matrix;

  const segments = [
    { label: "Direct", value: direct, color: "#c84bff" },
    { label: "Generation", value: gen, color: "#7c5cff" },
    { label: "Matrix", value: matrix, color: "#2ee6ff" },
  ];

  const refLink = `${window.location.origin}${window.location.pathname}?ref=${data.userId}`;
  const uplineId = data.details.referrer && !data.details.referrer.isZero()
    ? data.details.referrer.toString() : "—";

  return (
    <div className="stack">
      {data.paused && (
        <div className="card" style={{ borderColor: "rgba(255,181,71,0.5)" }}>
          <span style={{ color: "var(--warning)" }}>⚠ Platform paused — actions temporarily unavailable.</span>
        </div>
      )}

      {/* 1. USER SECTION */}
      <div className="card user-card">
        <SectionHeader icon="node" title="My Account" accent="violet" />
        <div className="grid grid-2">
          <div className="mini">
            <span className="muted">User ID</span>
            <strong className="grad-text">{data.userId.toString()}</strong>
          </div>
          <div className="mini">
            <span className="muted">Upline ID</span>
            <strong>{uplineId}</strong>
          </div>
          <div className="mini">
            <span className="muted">Wallet</span>
            <strong>{shortAddress(account)}</strong>
          </div>
          <div className="mini">
            <span className="muted">Current Package</span>
            <strong className="grad-text-cyan">
              {highest ? `$${highest.price.toLocaleString()}` : "—"}
            </strong>
          </div>
        </div>
        {highest && (
          <div className="plan-tag">
            <span className="plan-tag-ico"><Icon name="layers" size={15} /></span>
            {highest.label} · Tier #{highest.id}
          </div>
        )}
      </div>

      {/* 2. TOTAL INCOME */}
      <div className="hero hero-anim">
        <div className="hero-label">Total Income</div>
        <div className="hero-value">
          <CountUp value={totalIncome} decimals={2} />
          <span className="unit">USDT</span>
        </div>
      </div>

      {/* 3. REFERRAL LINK */}
      <div className="card">
        <SectionHeader icon="link" title="Your Referral Link" accent="cyan" />
        <CopyField value={refLink} />
      </div>

      {/* 4. TEAM + DIRECT COUNTS */}
      <div className="grid grid-2">
        <div className="stat stat-accent">
          <div className="label">Total Team</div>
          <div className="value grad-text"><CountUp value={data.totalTeam ? data.totalTeam.toNumber() : 0} decimals={0} /></div>
        </div>
        <div className="stat stat-accent">
          <div className="label">Direct Referrals</div>
          <div className="value grad-text-cyan"><CountUp value={data.details.totalDirectReferrals.toNumber()} decimals={0} /></div>
        </div>
      </div>

      {/* 5. CLAIM BANNER -> SCN */}
      <ClaimBanner claimable={data.scnStats ? data.scnStats.claimable : null} onClick={() => goTab("scn")} />

      {/* 6. INCOME BREAKDOWN */}
      <div className="card">
        <SectionHeader icon="coins" title="Income Breakdown" accent="success" />
        <div className="row" style={{ gap: 18, alignItems: "center" }}>
          <Donut segments={segments} centerTop={fmtUsdt(grossTotalBN)} centerBottom="USDT" />
          <div className="legend" style={{ flex: 1 }}>
            {segments.map((s) => (
              <div className="legend-item" key={s.label}>
                <span className="legend-dot" style={{ background: s.color }} />
                <span className="legend-label">{s.label}</span>
                <span className="legend-value">{incomeTotal > 0 ? Math.round((s.value / incomeTotal) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 7. RECENT ACTIVITY */}
      <RecentActivity userId={data.userId} />
    </div>
  );
}
